export const WORKER_OPS = [
  "unlock",
  "deriveAuth",
  "lock",
  "encrypt",
  "decrypt",
  "identityCreate",
  "identityLoad",
  "orgCreate",
  "orgOpen",
  "orgOpenInvite",
  "orgSealFor",
  "orgInviteCreate",
  "inviteDerive",
  "orgEncrypt",
  "orgDecrypt",
  "orgRotateBegin",
  "orgRotateSealFor",
  "orgRotateReencrypt",
  "orgRotateCommit",
  "orgRotateAbort",
  "titleHash",
  "keysOpen",
  "keysCreate",
  "keysSealFor",
  "recoveryCreate",
  "recoveryProof",
  "recoveryOpen",
  "escrowSeal",
  "instanceKeyCreate",
  "instanceKeyOpen",
  "instanceKeySealFor",
  "escrowReset",
  "passkeySeal",
  "passkeyOpen",
  "sealForExtension"
] as const;

export type WorkerOp = (typeof WORKER_OPS)[number];

interface OpRequest<Op extends WorkerOp> {
  readonly id: number;
  readonly op: Op;
}

interface OrgRequest<Op extends WorkerOp> extends OpRequest<Op> {
  readonly orgId: string;
}

/**
 * Derives the login proof and keeps the password's kek for keysOpen/keysCreate. The vault stays
 * locked until one of those loads the account keys.
 */
export interface UnlockRequest extends OpRequest<"unlock"> {
  readonly password: string;
  readonly saltB64: string;
  readonly email: string;
}

/** Derives the login proof only. Vault keys in the worker are left untouched. */
export interface DeriveAuthRequest extends OpRequest<"deriveAuth"> {
  readonly password: string;
  readonly saltB64: string;
  readonly email: string;
}

export interface LockRequest extends OpRequest<"lock"> {}

export interface EncryptRequest extends OpRequest<"encrypt"> {
  readonly plaintextB64: string;
  readonly aadB64: string;
}

export interface DecryptRequest extends OpRequest<"decrypt"> {
  readonly nonceB64: string;
  readonly ciphertextB64: string;
  readonly aadB64: string;
}

export interface IdentityCreateRequest extends OpRequest<"identityCreate"> {}

export interface IdentityLoadRequest extends OpRequest<"identityLoad"> {
  readonly pubB64: string;
  readonly privEncB64: string;
}

export interface OrgCreateRequest extends OrgRequest<"orgCreate"> {}

export interface OrgOpenRequest extends OrgRequest<"orgOpen"> {
  readonly wrappedB64: string;
}

export interface OrgOpenInviteRequest extends OrgRequest<"orgOpenInvite"> {
  readonly secretB64: string;
  readonly wrappedB64: string;
}

export interface OrgSealForRequest extends OrgRequest<"orgSealFor"> {
  readonly recipientPubB64: string;
}

export interface OrgInviteCreateRequest extends OrgRequest<"orgInviteCreate"> {}

export interface InviteDeriveRequest extends OpRequest<"inviteDerive"> {
  readonly secretB64: string;
}

export interface OrgEncryptRequest extends OrgRequest<"orgEncrypt"> {
  readonly plaintextB64: string;
  readonly aadB64: string;
}

export interface OrgDecryptRequest extends OrgRequest<"orgDecrypt"> {
  readonly nonceB64: string;
  readonly ciphertextB64: string;
  readonly aadB64: string;
}

export interface OrgRotateBeginRequest extends OrgRequest<"orgRotateBegin"> {}

export interface OrgRotateSealForRequest extends OrgRequest<"orgRotateSealFor"> {
  readonly recipientPubB64: string;
}

export interface OrgRotateReencryptRequest extends OrgRequest<"orgRotateReencrypt"> {
  readonly nonceB64: string;
  readonly ciphertextB64: string;
  readonly aadB64: string;
}

export interface OrgRotateEndRequest extends OrgRequest<"orgRotateCommit" | "orgRotateAbort"> {}

export interface TitleHashRequest extends OpRequest<"titleHash"> {
  /** "personal" for the vault key, otherwise an org id whose key is loaded. */
  readonly scope: string;
  readonly title: string;
}

/**
 * Loads the account keys sealed under the kek from unlock. Without keyEncB64 (account from before
 * the account keys) the password-derived keys become the account keys and come back sealed as keyEncB64.
 */
export interface KeysOpenRequest extends OpRequest<"keysOpen"> {
  readonly keyEncB64?: string | undefined;
}

/** New account: random account keys, returned sealed under the kek from unlock as keyEncB64. */
export interface KeysCreateRequest extends OpRequest<"keysCreate"> {}

/** Master password change: seals the loaded account keys under a new password. Returns authKeyB64 and keyEncB64. */
export interface KeysSealForRequest extends OpRequest<"keysSealFor"> {
  readonly password: string;
  readonly saltB64: string;
  readonly email: string;
}

/** New emergency kit for the loaded account keys. Returns recoveryKey (text), keyEncB64 and authKeyB64. */
export interface RecoveryCreateRequest extends OpRequest<"recoveryCreate"> {}

/** Login proof of a recovery key (text form). Returns authKeyB64. */
export interface RecoveryProofRequest extends OpRequest<"recoveryProof"> {
  readonly recoveryKey: string;
}

/** Loads the account keys from their seal under a recovery key. */
export interface RecoveryOpenRequest extends OpRequest<"recoveryOpen"> {
  readonly recoveryKey: string;
  readonly keyEncB64: string;
}

/** Admin recovery: seals the loaded account keys to the instance recovery public key. Returns escrowB64. */
export interface EscrowSealRequest extends OpRequest<"escrowSeal"> {
  readonly publicKeyB64: string;
}

/** Generates the instance recovery key and keeps it. Returns publicKeyB64 and sealedKeyB64 (the secret sealed to our identity). */
export interface InstanceKeyCreateRequest extends OpRequest<"instanceKeyCreate"> {}

/** Opens our grant of the instance recovery key and checks it against the published public key. */
export interface InstanceKeyOpenRequest extends OpRequest<"instanceKeyOpen"> {
  readonly publicKeyB64: string;
  readonly sealedKeyB64: string;
}

/** Seals the loaded instance recovery secret to another admin's identity key. Returns sealedKeyB64. */
export interface InstanceKeySealForRequest extends OpRequest<"instanceKeySealFor"> {
  readonly recipientPubB64: string;
}

/**
 * Opens an account's escrow with the loaded instance key and seals its account keys under a
 * one-time password. Returns authKeyB64 and keyEncB64. Our own vault keys stay untouched.
 */
export interface EscrowResetRequest extends OpRequest<"escrowReset"> {
  readonly escrowB64: string;
  readonly password: string;
  readonly saltB64: string;
  readonly email: string;
}

/** Passkey unlock: seals the loaded account keys under the key from a passkey's PRF output. Returns keyEncB64. */
export interface PasskeySealRequest extends OpRequest<"passkeySeal"> {
  readonly prfB64: string;
}

/** Passkey sign-in: opens the account keys with the PRF output and loads them, replacing any loaded keys. */
export interface PasskeyOpenRequest extends OpRequest<"passkeyOpen"> {
  readonly prfB64: string;
  readonly keyEncB64: string;
}

/**
 * Extension link: seals the loaded account keys plus the extension's new session to the
 * extension's one-time key. Returns sealedB64 only.
 */
export interface SealForExtensionRequest extends OpRequest<"sealForExtension"> {
  /** Request id, standard base64 (16 bytes). */
  readonly requestIdB64: string;
  readonly publicKeyB64: string;
  readonly serverUrl: string;
  readonly userId: string;
  readonly email: string;
  readonly token: string;
  readonly expiresAt: number;
  readonly saltB64: string;
}

export type WorkerRequest =
  | UnlockRequest
  | DeriveAuthRequest
  | LockRequest
  | EncryptRequest
  | DecryptRequest
  | IdentityCreateRequest
  | IdentityLoadRequest
  | OrgCreateRequest
  | OrgOpenRequest
  | OrgOpenInviteRequest
  | OrgSealForRequest
  | OrgInviteCreateRequest
  | InviteDeriveRequest
  | OrgEncryptRequest
  | OrgDecryptRequest
  | OrgRotateBeginRequest
  | OrgRotateSealForRequest
  | OrgRotateReencryptRequest
  | OrgRotateEndRequest
  | TitleHashRequest
  | KeysOpenRequest
  | KeysCreateRequest
  | KeysSealForRequest
  | RecoveryCreateRequest
  | RecoveryProofRequest
  | RecoveryOpenRequest
  | EscrowSealRequest
  | InstanceKeyCreateRequest
  | InstanceKeyOpenRequest
  | InstanceKeySealForRequest
  | EscrowResetRequest
  | PasskeySealRequest
  | PasskeyOpenRequest
  | SealForExtensionRequest;

export interface UnlockResult {
  readonly authKeyB64: string;
}

export interface LockResult {
  readonly locked: true;
}

export interface EncryptResult {
  readonly nonceB64: string;
  readonly ciphertextB64: string;
}

export interface DecryptResult {
  readonly plaintextB64: string;
}

/** Named base64/text fields returned by the team ops. Empty object for ops with no output. */
export type FieldsResult = Readonly<Record<string, string>>;

export type WorkerResult = UnlockResult | LockResult | EncryptResult | DecryptResult | FieldsResult;

export type WorkerResponse =
  | { readonly id: number; readonly ok: true; readonly result: WorkerResult }
  | { readonly id: number; readonly ok: false; readonly error: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isWorkerResponse(value: unknown): value is WorkerResponse {
  if (!isRecord(value)) {
    return false;
  }
  if (typeof value["id"] !== "number" || typeof value["ok"] !== "boolean") {
    return false;
  }
  if (value["ok"] === true) {
    return isRecord(value["result"]);
  }
  return typeof value["error"] === "string";
}

export function isUnlockResult(value: unknown): value is UnlockResult {
  return isRecord(value) && typeof value["authKeyB64"] === "string";
}

export function isLockResult(value: unknown): value is LockResult {
  return isRecord(value) && value["locked"] === true;
}

export function isEncryptResult(value: unknown): value is EncryptResult {
  return isRecord(value) &&
    typeof value["nonceB64"] === "string" &&
    typeof value["ciphertextB64"] === "string";
}

export function isDecryptResult(value: unknown): value is DecryptResult {
  return isRecord(value) && typeof value["plaintextB64"] === "string";
}

export function isFieldsResult(value: unknown): value is FieldsResult {
  if (!isRecord(value)) {
    return false;
  }
  return Object.values(value).every((field: unknown): boolean => typeof field === "string");
}
